export interface ProblemDetails {
  type: string | undefined;
  title: string | undefined;
  status?: number;
  detail: string | undefined;
  instance: string | undefined;
  extensions: Record<string, unknown>;
  errorCode: number;
}

/*
 * True if data is a ProblemDetails object (best guess)
 */
export function isProblemDetails(data: unknown): data is ProblemDetails {
  if (typeof data !== "object" || data === null) return false;
  const record = data as Record<string, unknown>;
  return typeof data === "object" && "type" in record && "title" in record && "status" in record && "detail" in record;
}

/*
 * Returns data as ProblemDetails type, else undefined
 */
export function getProblemDetails(data: unknown): ProblemDetails | undefined {
  if (isProblemDetails(data)) {
    return data as ProblemDetails;
  }
  return undefined;
}

/*
 * Returns the error message from a ProblemDetails object, or message from an Error object, else undefined
 */
export const getProblemDetailsOrErrorObjectMessage = (data: unknown): string | undefined => {
  const problemDetails = getProblemDetails(data);
  if (problemDetails) {
    return problemDetails.detail || problemDetails.title;
  }
  if (data instanceof Error) {
    return data.message;
  }
  return undefined;
};

/*
 * A short description of an error for logs: the status and title of a ProblemDetails, else an Error's message. Never
 * the ProblemDetails detail, which may repeat the values that were sent.
 */
export const getErrorLogText = (error: unknown): string => {
  const problemDetails = getProblemDetails(error);
  if (problemDetails) return `${problemDetails.status ?? ""} ${problemDetails.title ?? ""}`.trim();
  if (error instanceof Error) return error.message;
  return String(error);
};
